// Fully Updated RTO + Real Estate Engine with All 36 Rules Implemented

export const simulateRentToOwnRealEstate = (params) => {
    const {
        propertyValue,
        propertyGrowthRate,
        rentGrowthRate,
        taxRate,
        insuranceRate,
        maintenancePercentOfRent,
        rentPercent,
        equityPercent,
        sellCostRate,
        monthlyRentRatio,
        reinvestmentSplit,
    } = params;

    const rentPortion = rentPercent / 100;
    const equityPortion = equityPercent / 100;
    const taxRateMonthly = taxRate / 100 / 12;
    const insuranceRateMonthly = insuranceRate / 100 / 12;
    const maintenanceRate = maintenancePercentOfRent / 100;
    const sellCostRateDecimal = sellCostRate / 100;

    const monthlyData = [];
    const reinvestmentChunks = [];
    const nonReinvestmentChunks = [];

    let assetValue = propertyValue;
    let monthlyRent = assetValue / monthlyRentRatio;
    let equityInvestor = propertyValue;
    let equityEntrepreneur = 0;

    let cumNetRentIncome = 0;
    let cumRentIncome = 0;
    let cumInvestorExpenses = 0;
    let cumEntrepreneurExpenses = 0;
    let cumEquityBuyout = 0;
    let cumOffsetEquity = 0;
    let cumEquityFromExtra = 0;

    let quarterlyNetIncome = 0;
    let quarterlyEquityBuyout = 0;
    let reinvestedCapital = 0;
    let quarterlyOffsetEquity = 0;
    let nonReinvestedCash = 0;


    for (let month = 1; month <= 480; month++) {
        // ✅ Clamp negative equity
        if (equityInvestor < 0) equityInvestor = 0;
        if (equityEntrepreneur < 0) equityEntrepreneur = 0;
        let equityFromRent = monthlyRent * (1 - rentPortion);
        let equityFromExtra = monthlyRent * equityPortion;
        let rentCollected = monthlyRent * rentPortion;
        let totalEquity = equityInvestor + equityEntrepreneur;
        let investorShare = totalEquity > 0 ? equityInvestor / totalEquity : 1;
        let entrepreneurShare = 1 - investorShare;
        let entrepreneurOffset = entrepreneurShare * rentCollected;
        let investorRentIncome = rentCollected - entrepreneurOffset;

        let offsetEquity = entrepreneurOffset;

        let investorTax = assetValue * taxRateMonthly * investorShare;
        let investorInsurance = assetValue * insuranceRateMonthly * investorShare;
        let investorMaintenance = monthlyRent * maintenanceRate * investorShare;
        let investorNet = investorRentIncome - investorTax - investorInsurance - investorMaintenance;

        let entrepreneurTax = assetValue * taxRateMonthly * entrepreneurShare;
        let entrepreneurInsurance = assetValue * insuranceRateMonthly * entrepreneurShare;
        let entrepreneurMaintenance = monthlyRent * maintenanceRate * entrepreneurShare;
        let entrepreneurPayment = investorRentIncome + equityFromExtra + offsetEquity;

        let investorEquityRatio = equityInvestor / (equityInvestor + equityEntrepreneur);
        const remainingInvestorEquity = (equityInvestor + equityEntrepreneur) * investorEquityRatio;
        const currentEntrepreneurPayment = (equityFromExtra + offsetEquity + investorRentIncome);

        let totalEquityBuyout = equityFromExtra + offsetEquity;

        if (remainingInvestorEquity <= currentEntrepreneurPayment) {
            //assetValue *= (1 + propertyGrowthRate / 100);
            offsetEquity = remainingInvestorEquity;
            equityFromExtra = 0;
            rentCollected = 0;
            investorRentIncome = 0;
            entrepreneurTax = entrepreneurTax + investorTax;
            investorTax = 0;
            entrepreneurInsurance = entrepreneurInsurance + investorInsurance;
            investorInsurance = 0;
            entrepreneurMaintenance = entrepreneurMaintenance + investorMaintenance;
            investorMaintenance = 0;
            investorNet = 0;
            equityInvestor = 0;
            investorEquityRatio = equityInvestor / (equityInvestor + equityEntrepreneur);
            totalEquityBuyout = remainingInvestorEquity;
            investorShare = totalEquity > 0 ? equityInvestor / totalEquity : 1;
            cumEquityBuyout += remainingInvestorEquity;
            entrepreneurShare = 1 - investorShare;
        }
        else {
            equityInvestor -= totalEquityBuyout;
            equityEntrepreneur += totalEquityBuyout;
            cumEquityBuyout += totalEquityBuyout;
        }
        cumRentIncome += investorRentIncome;
        cumNetRentIncome += investorNet;
        cumInvestorExpenses += investorTax + investorInsurance + investorMaintenance;
        cumEntrepreneurExpenses += entrepreneurTax + entrepreneurInsurance + entrepreneurMaintenance;

        if (month % 12 === 1 && month > 1) {
            assetValue *= (1 + propertyGrowthRate / 100);
            monthlyRent = assetValue / monthlyRentRatio + (monthlyRent * rentGrowthRate / 100);
        }

        //const totalEquity = equityInvestor + equityEntrepreneur;
        const investorRatio = equityInvestor / totalEquity;
        const entrepreneurRatio = 1 - investorRatio;

        equityInvestor = assetValue * investorRatio;
        equityEntrepreneur = assetValue * entrepreneurRatio;

        // ✅ Move exit calculations ABOVE push()
        const closingCost = assetValue * sellCostRateDecimal;

        // const investorExitValue =
        //     (assetValue - closingCost) * investorShare +
        //     cumNetRentIncome + cumEquityBuyout;

        const entrepreneurExitValue = Math.max(
            0,
            (assetValue - closingCost) * entrepreneurShare - cumEntrepreneurExpenses
        );










        quarterlyNetIncome += investorNet;
        quarterlyEquityBuyout += equityFromExtra;
        quarterlyOffsetEquity += offsetEquity;

        if (month % 3 === 0) {
            const totalQuarterly = quarterlyNetIncome + quarterlyEquityBuyout + quarterlyOffsetEquity;
            const reinvestAmount = totalQuarterly * (reinvestmentSplit / 100);
            const nonReinvestedAmount = totalQuarterly * (1 - reinvestmentSplit / 100);

            if (reinvestAmount > 0) {
                reinvestmentChunks.push({
                    amount: +reinvestAmount.toFixed(2),
                    age: null,
                    monthAdded: month,
                    sourceNet: +quarterlyNetIncome.toFixed(2),
                    sourceEquity: +quarterlyEquityBuyout.toFixed(2),
                    sourceOffset: +quarterlyOffsetEquity.toFixed(2)
                });
            }

            // ✅ track uninvested portion as retained cash
            nonReinvestedCash += +nonReinvestedAmount.toFixed(2);

            if (nonReinvestedAmount > 0) {
                nonReinvestmentChunks.push({
                    amount: +nonReinvestedAmount.toFixed(2),
                    monthAdded: month,
                });
            }


            quarterlyNetIncome = 0;
            quarterlyEquityBuyout = 0;
            quarterlyOffsetEquity = 0;
        }

        let reinvestedValueAtExit = 0;
        // for (const chunk of reinvestmentChunks) {
        //     const monthsHeld = month - chunk.monthAdded;
        //     const yearsHeld = monthsHeld / 12;
        //     const grown = chunk.amount * Math.pow(1 + chunk.roiWhenAdded, yearsHeld);
        //     const net = grown * (1 - sellCostRateDecimal);
        //     reinvestedValueAtExit += net;
        // }
        //reinvestedCapital = reinvestedValueAtExit;

        const entrepreneurOwnership = equityEntrepreneur / (equityInvestor + equityEntrepreneur);

        // console.log("month", month);
        // console.log("asset", (assetValue - closingCost) * investorShare);
        // console.log("cumNetRentIncome", cumNetRentIncome);
        // console.log("cumEquityBuyout", cumEquityBuyout);
        // console.log("reinvestedValueAtExit", reinvestedValueAtExit);
        // console.log("investorShare", investorShare);
        const investorExitValue = (assetValue - closingCost) * investorShare + cumNetRentIncome + cumEquityBuyout + reinvestedValueAtExit;
        // const entrepreneurAssetShare = (assetValue - closingCost) * entrepreneurShare;
        // const entrepreneurTotalBuyout = cumEquityFromExtra + cumOffsetEquity;
        const entrepreneurROI = (cumEquityFromExtra + cumOffsetEquity) + cumEntrepreneurExpenses > 0
            ? ((assetValue - closingCost) * entrepreneurShare) / ((cumEquityFromExtra + cumOffsetEquity) + cumEntrepreneurExpenses)
            : 0;


        // Compute reinvestmentPool and reinvestmentPoolAgeBucket
        let reinvestmentPool = 0;




        monthlyData.push({
            month,
            assetValue: +assetValue.toFixed(2),
            monthlyRent: +monthlyRent.toFixed(2),
            "investorShare (%)": +(investorShare * 100).toFixed(4),
            "entrepreneurShare (%)": +(entrepreneurShare * 100).toFixed(4),
            investorEquity: +equityInvestor.toFixed(2),
            entrepreneurEquity: +equityEntrepreneur.toFixed(2),
            investorRentIncome: +investorRentIncome.toFixed(2),
            investorNetCashflow: +investorNet.toFixed(2),
            equityFromExtra: +equityFromExtra.toFixed(2),
            offsetEquity: +offsetEquity.toFixed(2),
            totalEquityBuyout: +totalEquityBuyout.toFixed(2),
            investorTax: +investorTax.toFixed(2),
            investorInsurance: +investorInsurance.toFixed(2),
            investorMaintenance: +investorMaintenance.toFixed(2),
            entrepreneurTax: +entrepreneurTax.toFixed(2),
            entrepreneurInsurance: +entrepreneurInsurance.toFixed(2),
            entrepreneurMaintenance: +entrepreneurMaintenance.toFixed(2),
            entrepreneurPayment: +entrepreneurPayment.toFixed(2),
            cumulativeNetRentIncome: +cumNetRentIncome.toFixed(2),
            cumulativeInvestorExpenses: +cumInvestorExpenses.toFixed(2),
            cumulativeEntrepreneurExpenses: +cumEntrepreneurExpenses.toFixed(2),
            cumulativeEquityBuyout: +cumEquityBuyout.toFixed(2),
            cumulativeRentIncome: +cumRentIncome.toFixed(2),
            closingCost: +closingCost.toFixed(2),
            investorExitValue: +investorExitValue.toFixed(2),
            entrepreneurExitValue: +entrepreneurExitValue.toFixed(2),
            reinvestedCapital: +reinvestedCapital.toFixed(2),
            entrepreneurROI: +entrepreneurROI.toFixed(4),
            reinvestmentPool: +reinvestmentPool.toFixed(2)
        });




        if (entrepreneurOwnership >= 1) break;


    }



    const final = monthlyData[monthlyData.length - 1];

    const termYears = final.month / 12;
    const trueInvestorROI = Math.pow((cumNetRentIncome + cumEquityBuyout + cumOffsetEquity) / propertyValue, 1 / termYears) - 1;



    const reinvestmentPoolAgeBucket = reinvestmentChunks.map(chunk => {
        const age = final.month - chunk.monthAdded;
        const growthFactor = Math.pow(1 + trueInvestorROI, age / 12);
        const rawGrowth = chunk.amount * growthFactor;
        const netAmount = rawGrowth * (1 - sellCostRateDecimal);

        return {
            monthAdded: chunk.monthAdded,
            amount: +chunk.amount.toFixed(2),
            age,
            roi: +trueInvestorROI.toFixed(4),
            growthFactor: +growthFactor.toFixed(6),
            rawGrowth: +rawGrowth.toFixed(2),
            netAmount: +netAmount.toFixed(2)
        };
    });


    reinvestedCapital = reinvestmentPoolAgeBucket.reduce((sum, chunk) => sum + chunk.amount, 0);
    final.reinvestmentPoolAgeBucket = reinvestmentPoolAgeBucket;
    final.reinvestedCapital = +reinvestedCapital.toFixed(2);


    const asset = final.assetValue;
    const year = final.month / 12 + 1;
    const ownership = final.investorEquity / asset;
    const sellCost = year > 1 ? asset * sellCostRateDecimal : 0;

    const reinvestedPortion = reinvestmentPoolAgeBucket
        //.filter(r => r.age >= (final.month - ((year - 1) * 12)))
        .reduce((sum, r) => sum + r.netAmount, 0);

    // const investorExit = (asset - sellCost) * ownership
    //                    + last.cumulativeNetRentIncome
    //                    + last.cumulativeEquityBuyout
    //                    + reinvestedPortion;
    // const investorExit = (asset - sellCost) * ownership
    //     + reinvestedPortion;


    const investorExit = (asset - sellCost) * ownership
        + reinvestedPortion
        + nonReinvestedCash;
    final.investorExitValue = investorExit.toFixed(2);





    // const totalReinvestedCapitalFromMonthlyData = monthlyData.reduce(
    //     (sum, m) => sum + m.investorRentIncome - m.investorInsurance - m.investorTax - m.investorInsurance + m.equityFromExtra + m.offsetEquity,
    //     0
    // );

    // const totalReinvestedCapitalFromMonthlyData = reinvestmentPoolAgeBucket.reduce(
    //     (sum, m) => sum + m.amount,
    //     0
    // );

    const totalReinvestedCapitalFromMonthlyData = monthlyData.reduce(
        (sum, m) => sum + m.entrepreneurOffsetEquity,
        0
    );


    const totalReinvestedCapital = reinvestmentChunks.reduce((sum, chunk) => sum + chunk.amount, 0);

    const equityBuyoutAudit = [];
    let runningCumulative = 0;

    monthlyData.forEach((m, idx) => {
        const prevCumulative = runningCumulative;
        runningCumulative += m.totalEquityBuyout;

        equityBuyoutAudit.push({
            month: m.month,
            equityBuyout: m.totalEquityBuyout,
            expectedCumulative: +runningCumulative.toFixed(2),
            recordedCumulative: m.cumulativeEquityBuyout,
            delta: +(m.cumulativeEquityBuyout - prevCumulative - m.totalEquityBuyout).toFixed(4)
        });
    });

    const sumOfBuyouts = monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout, 0);

    // console.log('Equity Buyout Audit Table:', equityBuyoutAudit);
    // console.log('Summation of totalEquityBuyout:', +sumOfBuyouts.toFixed(2));



    const yearlyData = [];
    for (let i = 0; i < monthlyData.length; i += 12) {
        const chunk = monthlyData.slice(i, i + 12);
        const last = chunk[chunk.length - 1];
        const asset = last.assetValue;
        const year = i / 12 + 1;
        const ownership = last.investorEquity / asset;
        const sellCost = year > 1 ? asset * sellCostRateDecimal : 0;

        const reinvestedCapital = reinvestmentPoolAgeBucket
            .filter(r => r.age >= (final.month - ((year) * 12)))
            .reduce((sum, r) => sum + r.amount, 0);

        const reinvestedPortion = reinvestmentPoolAgeBucket
            .filter(r => r.age >= (final.month - ((year) * 12)))
            .reduce((sum, r) => sum + r.netAmount, 0);

        const nonReinvestedPortion = nonReinvestmentChunks
            .filter(r => r.monthAdded <= (year * 12))
            .reduce((sum, r) => sum + r.amount, 0);


        // const investorExit = (asset - sellCost) * ownership
        //                    + last.cumulativeNetRentIncome
        //                    + last.cumulativeEquityBuyout
        //                    + reinvestedPortion;
        const investorExit = (asset - sellCost) * ownership
            + reinvestedPortion
            + nonReinvestedPortion;

        yearlyData.push({
            year: i / 12 + 1,
            assetValue: last.assetValue,
            investorEquity: last.investorEquity,
            entrepreneurEquity: last.entrepreneurEquity,
            investorExitValue: +investorExit.toFixed(2),
            entrepreneurExitValue: last.entrepreneurExitValue,
            rentIncome: chunk.reduce((sum, m) => sum + m.investorRentIncome, 0),
            investorNet: chunk.reduce((sum, m) => sum + m.investorNetCashflow, 0),
            equityBuyout: chunk.reduce((sum, m) => sum + m.totalEquityBuyout, 0),
            entrepreneurPaid: chunk.reduce((sum, m) => sum + m.entrepreneurPayment, 0),
            investorTax: chunk.reduce((sum, m) => sum + m.investorTax, 0),
            investorInsurance: chunk.reduce((sum, m) => sum + m.investorInsurance, 0),
            investorMaintenance: chunk.reduce((sum, m) => sum + m.investorMaintenance, 0),
            entrepreneurTax: chunk.reduce((sum, m) => sum + m.entrepreneurTax, 0),
            entrepreneurInsurance: chunk.reduce((sum, m) => sum + m.entrepreneurInsurance, 0),
            entrepreneurMaintenance: chunk.reduce((sum, m) => sum + m.entrepreneurMaintenance, 0),
            reinvestedCapital: +reinvestedCapital.toFixed(2),
            nonReinvestedCash: +nonReinvestedPortion.toFixed(2),
            cumulativeInvestorExpenses: last.cumulativeInvestorExpenses,
            cumulativeEntrepreneurExpenses: last.cumulativeEntrepreneurExpenses,
        });
    }

    
    const investorResult = {
        model: "Rent-to-Own + Real Estate",
        originalPropertyValue: propertyValue,
        finalValue: final.assetValue,
        exitValue: final.investorExitValue,
        annualizedROI: +(trueInvestorROI * 100).toFixed(2),
        irrEstimate: +(trueInvestorROI * 100).toFixed(2), // or customize if using more complex IRR logic
        annualNOI: +(cumNetRentIncome / termYears).toFixed(0),
        capRate: +((cumNetRentIncome / termYears) / propertyValue * 100).toFixed(1),

        netCashflow: final.cumulativeNetRentIncome,
        totalExpenses: final.cumulativeInvestorExpenses,
        reinvestedCapital: final.reinvestedCapital,
        cashOnCashReturn: +((final.cumulativeNetRentIncome / propertyValue) * 100).toFixed(2),
        totalROI: (((final.investorExitValue - propertyValue) / propertyValue) * 100).toFixed(2),
        termMonths: final.month,
        termYears: +(final.month / 12).toFixed(1),
        totalRentCollected: final.cumulativeRentIncome,
    };

    const borrowerResult = {
        model: "Rent-to-Own + Real Estate",
        equityBuilt: final.entrepreneurEquity,
        exitValue: final.entrepreneurExitValue,
        totalRentPaid: final.cumulativeRentIncome,
        totalExpenses: final.cumulativeEntrepreneurExpenses,
        //totalBuyout: final.cumulativeEquityBuyout.toFixed(2),
        totalBuyout: +(monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout, 0)).toFixed(2),
        totalPayment: +(final.cumulativeRentIncome + final.cumulativeEntrepreneurExpenses + final.cumulativeEquityBuyout).toFixed(2),
        monthlyRentPayment: +(monthlyData.reduce((sum, m) => sum + m.investorRentIncome, 0) / final.month).toFixed(2),
        monthlyPayment: +(monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout + m.investorRentIncome, 0) / final.month).toFixed(2),
        entrepreneurROI: final.entrepreneurROI,
        termMonths: final.month,
        termYears: +(final.month / 12).toFixed(1),
    };

    const totalROI = ((final.investorExitValue - propertyValue) / propertyValue);
    const annualizedROI = (Math.pow(1 + totalROI, 1 / (final.month / 12)) - 1) * 100;
    const irrEstimate = annualizedROI; // Approximation (valid since cashflows are even)

    investorResult.annualizedROI = +annualizedROI.toFixed(2);
    investorResult.irrEstimate = +irrEstimate.toFixed(2);

    const operatingExpenses = monthlyData.reduce((sum, m) =>
        sum + m.investorTax + m.investorInsurance + m.investorMaintenance, 0
    );

    const noi = final.cumulativeRentIncome - operatingExpenses;

    const annualNOI = noi / (final.month / 12);
    const capRate = (annualNOI / propertyValue) * 100;

    investorResult.annualNOI = Math.round(annualNOI);
    investorResult.capRate = +capRate.toFixed(2);

    // console.log(monthlyData);
    // console.log('Final Pool:', reinvestmentChunks);
    // console.log('Updated pool:', reinvestmentPoolAgeBucket);
    // console.log('cumNetRentIncome:', cumNetRentIncome);
    // console.log('cumEquityBuyout:', cumEquityBuyout);
    // console.log('cumOffsetEquity:', cumOffsetEquity);
    // console.log('propertyValue:', propertyValue);
    // console.log('trueInvestorROI:', trueInvestorROI);
    // console.log('totalReinvestedCapitalFromMonthlyData:', totalReinvestedCapitalFromMonthlyData);
    // console.log('ent equityBuilt:', final.entrepreneurEquity);
    // console.log('totalReinvestedCapital:', totalReinvestedCapital);

    return { monthlyData, yearlyData, investorResult, borrowerResult };
};
